import dayjs from 'dayjs';
import { useCallback, useId, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { REGISTER_ID_SEARCH_PARAM_KEY } from '@/modules/common/constants/search-params.ts';
import { useRegisterTypeStrings } from '@/modules/common/hooks/useRegisterTypeStrings.tsx';
import { useTransactionsListQuery } from '@/modules/issuer-dashboard/queries/transactions.ts';
import { SelectPaginate, SelectPaginateOption } from 'ui';
import { fixNameEnum } from 'ui/utils';
import { TransactionPaginateResponse, TransactionType } from 'api';

export type PaymentTransactionOption = SelectPaginateOption<TransactionPaginateResponse>;

interface PaymentTransactionSelectProps {
  disabled?: boolean;
  error?: string;
  label?: string;
  onChange: (option: PaymentTransactionOption | null) => void;
  typeExclude?: TransactionType[];
  value?: PaymentTransactionOption | null;
}

export const PaymentTransactionSelect = ({
  disabled = false,
  error,
  label = 'Select Transaction',
  onChange,
  typeExclude,
  value,
}: PaymentTransactionSelectProps) => {
  const [params] = useSearchParams();
  const registerId = +(params.get(REGISTER_ID_SEARCH_PARAM_KEY) || 0);
  const getRegisterTypeStrings = useRegisterTypeStrings();
  const cacheKey = useId();

  const { fetchNextPage } = useTransactionsListQuery(
    {
      page: 1,
      page_size: 15,
      register_id: registerId || undefined,
      type_exclude: typeExclude?.join(','),
    },
    true,
    registerId,
  );

  const getTransactionTypeLabel = useCallback(
    (type?: TransactionType) => {
      if (type === TransactionType.Subscription) {
        return getRegisterTypeStrings('Application', { capitalize: true });
      }

      if (type === TransactionType.Drp) {
        return 'Dividend reinvestment';
      }

      return fixNameEnum(type);
    },
    [getRegisterTypeStrings],
  );

  const mapTransactionToOption = useCallback(
    (item: TransactionPaginateResponse): PaymentTransactionOption => ({
      label: (
        <div className="flex justify-between pr-2 items-center gap-3">
          <span className="flex gap-1 items-center min-w-0">
            <span className="font-medium shrink-0">#{item.id}</span>
            <span className="truncate">{getTransactionTypeLabel(item.type)}</span>
            {item.register_class?.class_name && (
              <span className="text-gray-500 truncate">{item.register_class.class_name}</span>
            )}
          </span>
          <span className="text-xs text-gray-500 shrink-0">
            {dayjs(item.requested_at_date || item.issue_date).format('YYYY-MM-DD, HH:mm')}
          </span>
        </div>
      ),
      value: item,
    }),
    [getTransactionTypeLabel],
  );

  const initialOption = useMemo(() => {
    if (!value?.value) return null;

    return mapTransactionToOption(value.value);
  }, [mapTransactionToOption, value]);

  const handleFetchNextTransactions = useCallback(
    async (page: number): Promise<PaymentTransactionOption[]> => {
      const transactionsData = await fetchNextPage({ pageParam: page });
      const transactions = transactionsData?.data?.pages?.at(-1)?.data || [];

      return transactions.map(mapTransactionToOption);
    },
    [fetchNextPage, mapTransactionToOption],
  );

  return (
    <SelectPaginate
      value={initialOption}
      cacheUniqs={[registerId, cacheKey, JSON.stringify(typeExclude || [])]}
      label={label}
      error={error}
      onAsync={handleFetchNextTransactions}
      onChange={option => onChange(option as PaymentTransactionOption | null)}
      isClearable={true}
      disabled={!registerId || disabled}
    />
  );
};
